import styles from './style/Dashboard.module.scss';
import InnerLayout from '../Layout/InnerLayout';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MdExplore, MdPreview, MdClear, MdHelp } from 'react-icons/md';
import { format } from 'date-fns';

import {
  activatePlanPackage,
  deletePlanPackage,
  getPlanPackagesNew,
  getPlanPackagesPublishing,
  publishPlanPackage,
  unpublishPlanPackage,
  selectPlans,
  removeSuccess,
  removePublishError,
  removeCreateError,
} from '../../store/slice/manager/plans';
import { getDetail, selectMenu } from '../../store/slice/manager/menu';
import { selectPermissionSettings } from '../../store/slice/permissionSettings';

import { BiTrashAlt } from 'react-icons/bi';
import { RiCalendarEventFill } from 'react-icons/ri';
import moment from 'moment';

import Toast from '../Utilities/Toast';
import Tabs from '../Utilities/Tabs';
import Button from '../Utilities/Button';
import Alert from '../Utilities/Alert';
import ModalCreatePlan from './ModalCreatePlan';
import InputSearch from '../Utilities/InputSearch';
import Pagination from '../Utilities/PaginationNew';
// import MonthlyCalendar from "../Utilities/MonthlyCalender";
import Tips from '../Utilities/Tips';
import EmptyBox from '../Utilities/EmptyBox';
import { ClipLoader } from 'react-spinners';

import { toChineseNumber } from '../../utils';
import { getServiceName } from '../../constant/SERVICE_NAME_MAP';
import {
  collectPackagesByBillingType,
  filterPackagesByBillingType,
} from '../../constant/SERVICE_PACKAGE_TYPES';
import Datepicker from '../Utilities/Datepicker/Datepicker';

const formatPlanFee = (plan) => {
  const isYear = plan?.pay_type === 'year';
  // 年費直接取 rental_fee（或 real_rental_fee）；月費取 monthly_additional_fee。不再用月費 ×12。
  const amount = isYear
    ? Number(plan?.real_rental_fee ?? plan?.rental_fee ?? 0)
    : Number(plan?.monthly_additional_fee ?? 0);
  return {
    amount,
    unit: isYear ? '年' : '月',
    cycleLabel: isYear ? '年繳' : '月繳',
  };
};

const PlanManagement = ({ serviceId: propServiceId, isModal = false }) => {
  const params = useParams();
  // 一般路由使用 params.id；以 modal 嵌入時改用傳入的 serviceId。
  const serviceId = propServiceId != null ? String(propServiceId) : params.id;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { detail } = useSelector(selectMenu);
  const { auth, permissions, authLoading } = useSelector(selectPermissionSettings);
  const { packages, publish, loading, refresh, publishError, createError, packagesServiceId, packagesType, publishingByType, publishingByTypeServiceId } =
    useSelector(selectPlans);

  // 以 modal 嵌入服務管理時，操作者是最高管理者(workspace.admin)。
  // 後端建立/發佈方案的 API 只需登入即可（無 workspaceUser 權限中介層），
  // 因此在此情境放行前端權限鎖，讓最高管理者可直接建立/發佈/刪除方案。
  const canManageAll = isModal;

  // 有權限檢視方案的使用者，在方案資料尚未載入完成前顯示 loading，
  // 避免先渲染「無啟用方案／空列表」空狀態，資料回來後才跳成實際內容。
  const hasPackagePermission = canManageAll || permissions?.find((item) => item.includes('package'));

  // 方案種類分頁：訂閱(group_by_plan) / 席位(stored_value)。
  // 兩種型別的方案組合結構不同（席次 vs 面額），故分開切換、各自抓取與渲染。
  const [packageType, setPackageType] = useState('group_by_plan');
  const isStoredValue = packageType === 'stored_value';
  const otherType = isStoredValue ? 'group_by_plan' : 'stored_value';
  const PACKAGE_TYPE_TABS = ['訂閱', '席位方案'];
  const TYPE_LABEL = { group_by_plan: '訂閱', stored_value: '席位方案' };

  // 僅用 pkg.type 過濾列表，避免訂閱／席位資料混在一起；分頁一律保留。
  const plansReady =
    String(publishingByTypeServiceId) === String(serviceId) &&
    !!publishingByType?.[packageType] &&
    !!publishingByType?.[otherType] &&
    String(packagesServiceId) === String(serviceId) &&
    String(packagesType) === String(packageType);
  const isPlanLoading = authLoading || (hasPackagePermission && !plansReady);

  // 跨桶合併後再分類：舊 AI 席位資料可能被標成 group_by_plan，但仍應歸到席位分頁。
  const activeThisTypePackages = collectPackagesByBillingType(
    publishingByType,
    packageType
  ).filter((item) => item.publishing);
  const hasActiveThisType = activeThisTypePackages.length > 0;
  const hasActiveOtherType = collectPackagesByBillingType(
    publishingByType,
    otherType
  ).some((item) => item.publishing);
  const isTypeLocked = hasActiveOtherType;

  const unpublishedPackages = filterPackagesByBillingType(
    packages?.packages?.filter((item) => !item.publishing) || [],
    packageType
  );

  const [toastValue, setToastValue] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [fetchSearchValue, setFetchSearchValue] = useState('');
  const [page, setPage] = useState(0);
  const pageLimit = 15;
  const [tabValue, setTabValue] = useState(1);
  const [index, setIndex] = useState(null);

  const [createPlan, setCreatePlan] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [calendarShow, setCalendarShow] = useState(false);
  const [reserveIndex, setReserveIndex] = useState(null);

  const [id, setId] = useState(null);
  const [removeId, setRemoveId] = useState(null);
  const [cancelId, setCancelId] = useState(null);
  // 強制停用「目前生效中」方案的確認對話框；存待停用的 package id。
  const [deactivateId, setDeactivateId] = useState(null);
  const [status, setStatus] = useState('');
  const [init, setInit] = useState(true);
  const [planDate, setPlanDate] = useState({ startDay: '', endDay: '' });
  const [errorDate, setErrorDate] = useState(false);
  const [isTipShow, setIsTipShow] = useState('');

  // 席位（儲值金）啟用中方案的面額清單預設收合，點下拉才展開；記錄已展開的 package id。
  const [expandedFeePackages, setExpandedFeePackages] = useState([]);

  const tabList = ['企業帳號管理', '方案管理', '付費方式'];

  const handleStartDay = (date, startDay) => {
    const day = new Date(date);
    return format(day.setDate(day.getDate() - (startDay ? 1 : 0)), 'yyyy-MM-dd');
  };

  // 服務對象顯示標籤：後端值 center/company → 核心服務/企業服務。
  const SERVICE_TARGET_LABEL = { center: '核心服務', company: '企業服務' };
  // 方案組合的服務對象標籤：取自其 plan 的 type（center / company）。同一包內各 plan 的 type 一致。
  const getPlanTypeLabel = (pkg) => {
    const target = pkg?.plans?.find((p) => p?.type)?.type;
    return target ? SERVICE_TARGET_LABEL[target] ?? target : '';
  };
  const toggleFeePackage = (pkgId) =>
    setExpandedFeePackages((prev) => (prev.includes(pkgId) ? prev.filter((pid) => pid !== pkgId) : [...prev, pkgId]));

  useEffect(() => {
    dispatch(getDetail({ service: serviceId, group_plans: 1 }));
  }, [dispatch, serviceId]);

  // 切換服務時重設方案種類分頁，避免沿用上一個服務的席位／訂閱狀態。
  useEffect(() => {
    setPackageType('group_by_plan');
    setPage(0);
    setSearchTerm('');
    setFetchSearchValue('');
    setIndex(null);
    setCalendarShow(false);
    setExpandedFeePackages([]);
  }, [serviceId]);

  useEffect(() => {
    if (
      (init || refresh || publish || publishError || fetchSearchValue) &&
      (canManageAll || permissions?.find((item) => item.includes(detail?.sys_alias) && item.includes('package.view_package')))
    ) {
      dispatch(
        getPlanPackagesNew({
          service: serviceId,
          publishing: 0,
          limit: pageLimit,
          page: page + 1,
          search: fetchSearchValue,
          type: packageType,
        })
      );
      dispatch(getPlanPackagesPublishing({ service: serviceId, type: packageType }));
      dispatch(getPlanPackagesPublishing({ service: serviceId, type: otherType }));
      setInit(false);

      if (refresh) setTimeout(() => dispatch(removeSuccess()), 1200);
      if (publish) setTimeout(() => dispatch(removeSuccess()), 1200);
      if (publishError) {
        setTimeout(() => {
          dispatch(removePublishError());
          setStatus('');
        }, 1500);
      }
    }
  }, [dispatch, page, serviceId, refresh, publish, publishError, fetchSearchValue, permissions, detail?.sys_alias, init, canManageAll, packageType, otherType]);

  // 新增方案失敗的錯誤 Toast，顯示 3 秒後自動清除。
  useEffect(() => {
    if (!createError) return;
    const timer = setTimeout(() => dispatch(removeCreateError()), 3000);
    return () => clearTimeout(timer);
  }, [dispatch, createError]);

  const handleChangePage = (e) => {
    setPage(e.selected);
    dispatch(
      getPlanPackagesNew({
        service: serviceId,
        publishing: 0,
        limit: pageLimit,
        page: e.selected + 1,
        search: fetchSearchValue,
        type: packageType,
      })
    );
  };

  // 切換方案種類（一般方案／儲值金）：重設分頁與搜尋，並依新型別重新抓取啟用中／未啟用清單。
  const handleChangePackageType = (type) => {
    if (type === packageType) return;
    setPackageType(type);
    setPage(0);
    setSearchTerm('');
    setFetchSearchValue('');
    setIndex(null);
    setCalendarShow(false);
    dispatch(
      getPlanPackagesNew({
        service: serviceId,
        publishing: 0,
        limit: pageLimit,
        page: 1,
        search: '',
        type,
      })
    );
    dispatch(getPlanPackagesPublishing({ service: serviceId, type }));
    dispatch(getPlanPackagesPublishing({ service: serviceId, type: type === 'stored_value' ? 'group_by_plan' : 'stored_value' }));
  };

  const handleSuccess = () => {
    dispatch(activatePlanPackage(id)).then((e) => {
      if (e.payload.msg === 'success') {
        restart();
      }
    });
    setId(null);
  };

  const handleRemoveSuccess = () => {
    dispatch(deletePlanPackage(removeId)).then((e) => {
      if (e.payload.msg === 'success') {
        restart();
      }
    });
    setRemoveId(null);
  };

  const handleCancelSuccess = () => {
    const postObj = {
      id: reserveIndex,
      publish_month: null,
    };
    dispatch(publishPlanPackage(postObj)).then((e) => {
      if (e.payload.msg === 'success') {
        restart();
      }
    });

    setCancelId(null);
  };

  // 強制停用目前生效中的方案：後端會把 publishing / publish_at 清掉，回到未啟用草稿。
  const handleDeactivateSuccess = () => {
    dispatch(unpublishPlanPackage(deactivateId)).then((e) => {
      if (e.payload?.msg === 'success') {
        restart();
        // 重新抓兩種類型的啟用中方案，讓啟用卡片消失、跨類型鎖定（isTypeLocked）同步更新。
        dispatch(getPlanPackagesPublishing({ service: serviceId, type: packageType }));
        dispatch(getPlanPackagesPublishing({ service: serviceId, type: otherType }));
      }
    });
    setDeactivateId(null);
  };

  useEffect(() => {
    // modal 嵌入時沒有頁面分頁，不做路由跳轉。
    if (isModal) return;
    switch (tabValue) {
      case 0:
        return navigate('/dashboard/business/' + params.id);
      case 1:
        return navigate('/dashboard/plan-management/' + params.id);
      case 2:
        return navigate('/dashboard/payment/' + params.id);
      default:
        return null;
    }
  }, [tabValue, navigate, params.id, isModal]);

  const restart = () => {
    setFetchSearchValue('');
    let parameters = {
      service: serviceId,
      publishing: 0,
      limit: pageLimit,
      page: page + 1,
      search: '',
      type: packageType,
    };
    dispatch(getPlanPackagesNew(parameters));
  };

  const handleConfirm = (date) => {
    setCalendarShow(false);
    const postObj = {
      id: reserveIndex,
      publish_date: handleStartDay(planDate.startDay),
      publish_end_date: null, //handleStartDay(planDate.endDay),
      // publish_month: moment(date).format("YYYY-MM"),
    };
    dispatch(publishPlanPackage(postObj)).then((e) => {
      if (e.payload.msg === 'success') {
        restart();
        setStartDate(date);
      }
    });
  };

  const content = (
    <>
      <Toast
        title={
          createError
            ? '新增方案組合失敗'
            : status === 'create'
            ? '新增方案組合成功'
            : status === 'delete'
            ? '刪除方案組合成功'
            : status === 'publish' && !publishError
            ? `預計於「${moment(startDate).format('YYYY-MM')}號啟用」`
            : status === 'publishCancel'
            ? `取消預約成功`
            : status === 'deactivate'
            ? '已停用方案組合'
            : status === 'publish' && publishError
            ? '此月份已有其他預約方案'
            : ''
        }
        subtitle={createError ? createError : toastValue ? toastValue : ''}
        show={refresh || publish || publishError || Boolean(createError)}
        error={Boolean(createError)}
      />
      <Alert
        isOpen={id}
        onConfirm={handleSuccess}
        onClose={() => {
          setId(null);
        }}
        rightButtonText={'啟用'}
        leftButtonText='取消'
      />
      <Alert
        mainTitle={'清空所有設定?'}
        subTitle={'系統將會清空所有設定'}
        isOpen={removeId}
        onConfirm={handleRemoveSuccess}
        onClose={() => {
          setRemoveId(null);
        }}
        rightButtonText='清空'
        leftButtonText='取消'
      />
      <Alert
        mainTitle={'取消預約?'}
        subTitle={'系統將會取消預約'}
        isOpen={cancelId}
        onConfirm={handleCancelSuccess}
        onClose={() => {
          setCancelId(null);
        }}
        rightButtonText={'確認'}
        leftButtonText='取消'
      />
      <Alert
        mainTitle={'強制停用此方案?'}
        subTitle={'停用後方案將立即失效，並回到未啟用列表，可重新預約或啟用。'}
        isOpen={deactivateId}
        onConfirm={handleDeactivateSuccess}
        onClose={() => {
          setDeactivateId(null);
        }}
        rightButtonText='停用'
        leftButtonText='取消'
      />

      <div className={styles.dashboard}>
        <div className={styles.dashboardPlanManagement}>
          {!isModal && <div className={styles.dashboardTitle}>{getServiceName(detail?.name)}</div>}
          {!isModal && (
            <div className={styles.tabsContainer}>
              <Tabs values={tabList} onChange={(e) => setTabValue(e.tabIndex)} selectedIndex={tabValue} />
            </div>
          )}

          {isPlanLoading ? (
            <div className={styles.loadingWrapper}>
              <ClipLoader />
            </div>
          ) : (
            <>
              {canManageAll || permissions?.find((item) => item.includes('package')) ? (
                <>
                  {/* 方案種類切換：訂閱 / 席位方案 */}
                  <div className={styles.packageTypeTabs}>
                    <Tabs
                      values={PACKAGE_TYPE_TABS}
                      selectedIndex={isStoredValue ? 1 : 0}
                      onChange={(e) => handleChangePackageType(e.tabIndex === 1 ? 'stored_value' : 'group_by_plan')}
                    />
                  </div>
                  {/* 啟用中方案組合（同一服務同時只能有一個生效方案；此處為本類型的生效方案） */}
                  {hasActiveThisType ? (
                    // 訂閱／席位擇一 + 只能一個生效：正常情況下本類型至多一個生效方案。
                    activeThisTypePackages
                      .map((pkg) => {
                        const planTypeLabel = getPlanTypeLabel(pkg);
                        const feePlans = pkg.plans?.filter((item) => !item.is_trial) || [];
                        const feeExpanded = expandedFeePackages.includes(pkg.id);
                        return (
                          <div className={styles.isEnabledPlan} key={`enabled-${pkg.id}`}>
                            <div className={styles.enabledHeader}>
                              <div className={styles.enableShow}>目前啟用中</div>
                              <div className={styles.titleBox}>
                                <div className={styles.title}>
                                  {pkg.name}
                                  {planTypeLabel && <span className={styles.planTypeTag}>{planTypeLabel}</span>}
                                </div>
                                <div className={styles.date}>
                                  啟用日：
                                  <span>{moment(pkg.publish_at).format('YYYY/MM/DD')}</span>
                                </div>
                              </div>
                              {/* 強制停用目前生效中的方案（權限比照預約/啟用）。 */}
                              {(canManageAll ||
                                permissions?.find((item) => item.includes(detail?.sys_alias) && item.includes('package.appointment_package'))) && (
                                <div style={{ marginLeft: 'auto' }}>
                                  <Button
                                    type='secondary'
                                    title={'停用'}
                                    disabled={loading}
                                    onClick={() => {
                                      setDeactivateId(pkg.id);
                                      setToastValue(pkg.name);
                                      setStatus('deactivate');
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                            <div className={styles.enableBody}>
                              {isStoredValue ? (
                                // 席位（儲值金）：面額預設收合，點下拉才展開下面的面額清單。
                                <>
                                  <div className={styles.feeToggle} onClick={() => toggleFeePackage(pkg.id)}>
                                    <span>面額（{feePlans.length} 筆）</span>
                                    <span className={styles.feeToggleArrow}>{feeExpanded ? '▾' : '▸'}</span>
                                  </div>
                                  {feeExpanded && (
                                    <div className={styles.planOuter}>
                                      {feePlans.map((plan, idx) => (
                                        <div className={styles.enablePlan} key={`fee-${pkg.id}-${idx}`}>
                                          <div className={styles.planCount}>面額{toChineseNumber(idx + 1)}</div>
                                          <div className={styles.name}>{plan.name}</div>
                                          <div className={styles.bundledBox}>
                                            <div className={`${styles.bundledOuter} ${styles.plan}`}>
                                              <div className={styles.bundledOuterBottom}>
                                                <div className={styles.bundleBox} style={{ width: '145px' }}>
                                                  <div className={styles.title}>費用 (元)</div>
                                                  <div className={styles.fee}>NT${plan.rental_fee}</div>
                                                </div>
                                                <div className={styles.bundleBox} />
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className={styles.planOuter}>
                                  {feePlans.map((plan, idx) => (
                                    <div className={styles.enablePlan} key={`enableplan-${pkg.id}-${idx}`}>
                                      <div className={styles.planCount}>產品服務{toChineseNumber(idx + 1)}</div>
                                      <div className={styles.name}>{plan.name}</div>
                                      <div className={styles.bundledBox}>
                                        <div className={`${styles.bundledOuter} ${styles.plan}`}>
                                          <div className={styles.bundledOuterTop}>
                                            <div className={styles.bundleBox} style={{ width: '110px' }}>
                                              <div className={styles.days} style={{ width: 'max-content' }}>
                                                綁約 {plan.validity_days} 天
                                              </div>
                                            </div>
                                            <div className={styles.bundleBox} style={{ width: '145px' }}>
                                              <div className={styles.title}>基本人數</div>
                                              <div className={styles.fee}>{plan.users_count_upper_limit}</div>
                                            </div>
                                            <div className={styles.bundleBox} style={{ width: '145px' }}>
                                              <div className={styles.title}>增加帳號數級距</div>
                                              <div className={styles.fee}>{plan.additional_people_per_unit}</div>
                                            </div>
                                            <div className={styles.bundleBox} />
                                          </div>
                                          <div className={styles.bundledOuterBottom}>
                                            <div style={{ width: '110px' }} />
                                            <div className={styles.bundleBox} style={{ width: '145px' }}>
                                              <div className={styles.title}>費用(元)每人/{formatPlanFee(plan).unit}（{formatPlanFee(plan).cycleLabel}）</div>
                                              <div className={styles.fee}>NT${formatPlanFee(plan).amount}</div>
                                            </div>
                                            <div className={styles.bundleBox} style={{ width: '145px' }}>
                                              <div className={styles.title}>折扣 (%)</div>
                                              <div className={styles.discount}>{100 - plan.discount}%</div>
                                            </div>
                                            <div className={styles.bundleBox} />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                  ) : hasActiveOtherType ? (
                    // 訂閱／席位擇一：另一類型已有生效方案，本類型不可再啟用。
                    <div className={styles.planEnable}>
                      <div className={styles.nonEnableContainer}>
                        <div className={styles.nonEnale}>此服務目前已啟用「{TYPE_LABEL[otherType]}」方案</div>
                        <div className={styles.nonEnableAlert}>
                          訂閱與席位同時只能啟用一種。若要改用「{TYPE_LABEL[packageType]}」，請先至「{TYPE_LABEL[otherType]}」分頁取消其方案。
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.planEnable}>
                      <div className={styles.nonEnableContainer}>
                        <div className={styles.nonEnale}>目前無啟用中的方案組合</div>
                        <div className={styles.nonEnableAlert}>請從未啟用方案組合列表中選擇並立即啟用</div>
                      </div>
                    </div>
                  )}
                  {/* 未啟用方案組合 */}
                  <div className={styles.nonStartPlan}>
                    <div className={styles.nonStartPlanTitle}>未啟用的方案組合</div>
                    <div className={styles.nonStartFlex}>
                      <div className={styles.searchBar}>
                        <InputSearch
                          disabled={fetchSearchValue === '' && packages?.rows?.length === 0}
                          placeholder='搜尋方案組合名稱'
                          value={searchTerm}
                          onKeyPress={(e) => (e.code === 'Enter' || e.charCode === 13) && !loading && setFetchSearchValue(searchTerm)}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          clickSearchIcon={() => {
                            if (!loading) {
                              setFetchSearchValue(searchTerm);
                              setPage(0);
                            }
                          }}
                          onClear={() => {
                            restart();
                            setSearchTerm('');
                          }}
                          width={'220px'}
                        />
                      </div>

                      <Button
                        type='tertiary'
                        title={'新增方案組合'}
                        onClick={() => {
                          setCalendarShow(false);
                          setCreatePlan(true);
                        }}
                        // 訂閱／席位擇一：另一類型已有生效方案時鎖住；否則再判斷新增方案的權限。
                        disabled={
                          isTypeLocked ||
                          (!canManageAll &&
                            auth?.workspace_permissions?.length > 0 &&
                            !permissions?.find((item) => item.includes(detail?.sys_alias) && item.includes('package.create_package')))
                        }
                      />
                    </div>
                  </div>
                  {createPlan && (
                    <ModalCreatePlan
                      createPlan={createPlan}
                      setCreatePlan={setCreatePlan}
                      setStatus={setStatus}
                      setToastValue={setToastValue}
                      restart={restart}
                      serviceId={serviceId}
                      defaultType={packageType}
                    />
                  )}
                  {/* 方案table */}
                  <div className={styles.tableContainer}>
                    <table>
                      <thead>
                        <tr>
                          <th className={styles.reserve}>預約啟用日期</th>
                          <th className={styles.name}>方案組合名稱</th>
                          <th className={styles.time}>建立時間</th>
                          <th className='text-left'>方案數量</th>
                          <th className={`${styles.icons}`} colSpan={2}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {unpublishedPackages.length > 0 ? (
                          unpublishedPackages
                            ?.filter((_, index) => {
                              const packagesLength = unpublishedPackages.length;
                              if (packagesLength < pageLimit + 1) {
                                return true;
                              } else if (packagesLength > pageLimit) {
                                return index > packagesLength - pageLimit;
                              } else {
                                return false;
                              }
                            })
                            .map((data, i) => (
                              <tr key={i}>
                                <td className={`${styles.tableTd} text-center`} onClick={() => setReserveIndex(data.id)}>
                                  {data.publish_at && moment(data.publish_at) > moment(new Date()) ? (
                                    <div className={styles.btnFlex}>
                                      <div className={styles.reserveDate}>{data.publish_at && moment(data.publish_at).format('YYYY-MM-DD')}</div>
                                      {(canManageAll ||
                                        (auth?.workspace_permissions?.length > 0 &&
                                          permissions?.find((item) => item.includes(detail?.sys_alias) && item.includes('package.delete_package')))) && (
                                          <Button
                                            type='tertiary'
                                            title={'取消'}
                                            disabled={loading}
                                            onClick={() => {
                                              setCancelId(data.id);
                                              setToastValue(data.name);
                                              setStatus('publishCancel');
                                            }}
                                          />
                                        )}
                                    </div>
                                  ) : (
                                    <div className={styles.reserveWrapper}>
                                      {(canManageAll || auth?.workspace_permissions?.length > 0) && (
                                        <>
                                          <Button
                                            icon={<RiCalendarEventFill className={styles.reserveDate} />}
                                            type='tertiary'
                                            title='預約'
                                            onClick={() => {
                                              setPlanDate({
                                                startDay: '',
                                                endDay: null,
                                              });
                                              setCalendarShow(!calendarShow);
                                              setStartDate('');
                                              setStatus('publish');
                                              setToastValue(data.name);
                                            }}
                                            // 訂閱／席位擇一：另一類型有生效方案時鎖住；否則判斷使用者是否有預約的權限
                                            disabled={
                                              loading ||
                                              isTypeLocked ||
                                              (!canManageAll &&
                                                !permissions?.find(
                                                  (item) => item.includes(detail?.sys_alias) && item.includes('package.appointment_package')
                                                ))
                                            }
                                          />
                                          {calendarShow && data.id === reserveIndex && (
                                            <div className={styles.planPeriodLayout}>
                                              <div className={styles.planPeriod}>
                                                方案時間
                                                <Datepicker
                                                  selectDate={planDate.startDay}
                                                  error={errorDate}
                                                  setSelectDate={(e) =>
                                                    setPlanDate({
                                                      ...planDate,
                                                      startDay: e,
                                                    })
                                                  }
                                                  startYear={`${handleStartDay(new Date()).split('-')[0]}`}
                                                  disableEvents={[
                                                    {
                                                      start: `${handleStartDay(new Date())}`,
                                                      end: `2100-12-31`,
                                                    },
                                                  ]}
                                                />
                                                {/* 至
                                                  <Datepicker
                                                    selectDate={planDate.endDay}
                                                    setSelectDate={(e) =>
                                                      setPlanDate({
                                                        ...planDate,
                                                        endDay: e,
                                                      })
                                                    }
                                                    startYear={`${handleStartDay(new Date()).split("-")[0]}`}
                                                    disableEvents={[
                                                      {
                                                        start: `${
                                                          planDate.startDay !==
                                                          ""
                                                            ? handleStartDay(
                                                                planDate.startDay,
                                                                true
                                                              )
                                                            : handleStartDay(
                                                                new Date()
                                                              )
                                                        }`,
                                                        end: `2100-12-31`,
                                                      },
                                                    ]}
                                                  /> */}
                                              </div>
                                              <div className={styles.planPeriodButton}>
                                                <Button
                                                  type='secondary'
                                                  title={'取消'}
                                                  onClick={() => {
                                                    setCalendarShow(!calendarShow);
                                                  }}
                                                />
                                                <Button
                                                  type='tertiary'
                                                  title={'完成'}
                                                  onClick={() => {
                                                    if (planDate.startDay === '') {
                                                      setErrorDate(true);
                                                    } else {
                                                      setErrorDate(false);
                                                      handleConfirm();
                                                    }
                                                  }}
                                                />
                                              </div>
                                            </div>
                                          )}
                                          {/* <MonthlyCalendar
                                            calenderShow={
                                              calendarShow &&
                                              data.id === reserveIndex
                                            }
                                            setCalenderShow={setCalendarShow}
                                            handleConfirm={handleConfirm}
                                          /> */}
                                        </>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className={styles.tableTd}>
                                  {data.name}
                                  {getPlanTypeLabel(data) && <span className={styles.planTypeTag}>{getPlanTypeLabel(data)}</span>}
                                </td>
                                <td className={styles.tableTd}>{moment(data.created_at).format('YYYY-MM-DD')}</td>
                                <td className={styles.tableTd}>{data?.plans?.length}</td>

                                <td className={styles.tableTd}>
                                  <div className={styles.iconsCell}>
                                    <Tips detail={'查看方案'}>
                                      <Button
                                        onClick={() => {
                                          setCalendarShow(false);
                                          setIndex(i);
                                          // 席位方案：查看時面額預設收合，需點擊展開。
                                          if (isStoredValue) {
                                            setExpandedFeePackages((prev) => prev.filter((id) => id !== data.id));
                                          }
                                        }}
                                        type={'pure'}
                                        icon={<MdPreview />}
                                      />
                                    </Tips>

                                    <Button
                                      onClick={() => {
                                        setToastValue(data.name);
                                        setStatus('delete');
                                        setRemoveId(data.id);
                                      }}
                                      type={'pure'}
                                      icon={<BiTrashAlt />}
                                      disabled={
                                        loading ||
                                        (!canManageAll &&
                                          !permissions?.find((item) => item.includes(detail?.sys_alias) && item.includes('package.delete_package')))
                                      }
                                    />
                                  </div>

                                  {index === i && (
                                    <div className={styles.planReviewOuter}>
                                      <div className={styles.header}>
                                        <div className={styles.title}>查看方案</div>
                                        <Button
                                          onClick={() => {
                                            setIndex();
                                          }}
                                          type='pure'
                                          icon={<MdClear />}
                                        />
                                      </div>
                                      <div className={styles.body}>
                                        <>
                                          {data?.plans?.find((plan) => plan.is_trial) && (
                                            <div className={styles.trialPlanOuter}>
                                              <div className={styles.explore}>
                                                <MdExplore />
                                              </div>
                                              <div className={styles.name}>{data?.plans?.find((plan) => plan?.is_trial)?.name}</div>
                                              <div className={styles.detailsBoxOuter}>
                                                <div className={styles.detail}>
                                                  <div className={styles.title}>試用人數上限</div>
                                                  <div className={styles.count}>
                                                    {data?.plans?.find((plan) => plan.is_trial).users_count_upper_limit}
                                                    <span>人</span>
                                                  </div>
                                                </div>
                                                <div className={styles.detail}>
                                                  <div className={styles.title}>試用天數</div>
                                                  <div className={styles.count}>
                                                    {data?.plans?.find((plan) => plan.is_trial).plans[0]?.validity_days}
                                                    <span>天</span>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          )}

                                          {(() => {
                                            const reviewFeePlans = data?.plans?.filter((item) => !item.is_trial) || [];
                                            const reviewFeeExpanded = expandedFeePackages.includes(data.id);

                                            if (isStoredValue) {
                                              return (
                                                <>
                                                  <div className={styles.feeToggle} onClick={() => toggleFeePackage(data.id)}>
                                                    <span>面額（{reviewFeePlans.length} 筆）</span>
                                                    <span className={styles.feeToggleArrow}>{reviewFeeExpanded ? '▾' : '▸'}</span>
                                                  </div>
                                                  {reviewFeeExpanded &&
                                                    reviewFeePlans.map((plan, planIndex) => (
                                                      <div className={styles.planOuter} key={`plan${planIndex}`}>
                                                        <div className={styles.planCount}>面額{toChineseNumber(planIndex + 1)}</div>
                                                        <div className={styles.planName}>{plan.name}</div>
                                                        <div className={styles.tagOuter}>
                                                          <div className={styles.tag}>{`費用 (元) NT$ ${plan?.rental_fee}`}</div>
                                                        </div>
                                                      </div>
                                                    ))}
                                                </>
                                              );
                                            }

                                            return reviewFeePlans.map((plan, planIndex) => (
                                              <React.Fragment key={planIndex}>
                                                <div className={styles.planOuter} key={`plan${planIndex}`}>
                                                  <div className={styles.planCount}>
                                                    方案
                                                    {toChineseNumber(planIndex + 1)}
                                                  </div>
                                                  <div className={styles.planName}>{plan.name}</div>
                                                  <div className={styles.tagOuter}>
                                                    <div className={styles.tag}>
                                                      {plan?.users_count_upper_limit
                                                        ? `基本人數 ${plan?.users_count_upper_limit} 人`
                                                        : `${plan?.users_count_lower_limit}人以上`}
                                                    </div>
                                                    <div className={styles.tag}>{`費用(元)每人/${formatPlanFee(plan).unit}（${formatPlanFee(plan).cycleLabel}）NT$ ${formatPlanFee(plan).amount}`}</div>
                                                    <div className={styles.tag}>{`增加帳號數級距 ${plan?.additional_people_per_unit} 人`}</div>
                                                    <div className={`${styles.tag} ${styles.discount}`}>
                                                      {`折扣 ${100 - plan?.discount} %`}
                                                      <div className={styles.helpBtnOuter}>
                                                        <Button
                                                          type='pure'
                                                          icon={<MdHelp />}
                                                          onMouseEnter={() => setIsTipShow(planIndex)}
                                                          onMouseLeave={() => setIsTipShow(false)}
                                                          color={'#B4B4B4'}
                                                        />
                                                        {isTipShow === planIndex && (
                                                          <div className={styles.tips}>
                                                            <span>設定 100% ，完全無折扣，</span>
                                                            <span>設定 0%，整筆折扣掉，付款金額為0。</span>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                  {plan?.plans?.map((item, nestedIndex) => (
                                                    <div className={styles.bundledOuter} key={`bundledPlan_${nestedIndex}`}>
                                                      <div className={styles.days}>
                                                        綁約
                                                        {item.validity_days}天
                                                      </div>
                                                      <div className={styles.bundleBox}>
                                                        <div className={styles.title}>基本費用 (元)</div>
                                                        <div className={styles.fee}>
                                                          NT$
                                                          {item.rental_fee}
                                                        </div>
                                                      </div>
                                                      <div className={styles.bundleBox}>
                                                        <div className={styles.title}>折扣 (%)</div>
                                                        <div className={styles.discount}>{item.discount}%</div>
                                                      </div>
                                                      <div className={styles.bundleBox}></div>
                                                    </div>
                                                  ))}
                                                </div>
                                              </React.Fragment>
                                            ));
                                          })()}
                                        </>
                                      </div>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))
                        ) : (
                          <tr>
                            <td colSpan={7}>
                              <EmptyBox caption={fetchSearchValue ? '尚無搜尋結果' : '目前沒有方案組合'} type={1} />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    <Pagination
                      onPageChange={handleChangePage}
                      pageCount={page + 1}
                      totalData={packages?.packages_count}
                      limit={pageLimit}
                      totalPages={Math.ceil(packages?.packages_count / pageLimit)}
                    />
                  </div>
                </>
              ) : (
                <div className={styles.tableContainer}>
                  <EmptyBox caption='您沒有權限' />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );

  return isModal ? <div className={styles.planInModal}>{content}</div> : <InnerLayout>{content}</InnerLayout>;
};

export default PlanManagement;
